import React, { Fragment, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { UserCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../../hooks/useTheme';
import { useAuth } from '../../../contexts/AuthContext';
import { stripApiSuffix } from '../../../utils/apiBaseUrl';
import { resolveNexusChatLoginUrl } from '../../../utils/nexusChatUrl';
import VidexaLogo from '../../common/VidexaLogo';
import ReskinFonts from '../../common/ReskinFonts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3050/api';

// Plain-link fallback for when SSO token generation fails: the dev/prod Nexus
// LOGIN page, resolved by environment. Never a localhost URL (see nexusChatUrl).
const NEXUSCHAT_LOGIN_URL = resolveNexusChatLoginUrl();

/**
 * Header component with navigation and theme toggle
 * Uses HeadlessUI Menu component for reliable dropdown functionality
 */
const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout, isAuthenticated, getValidToken } = useAuth();
  const navigate = useNavigate();
  const [isNavigatingToNexus, setIsNavigatingToNexus] = useState(false);

  /**
   * Handle navigation to NexusChat with SSO
   * Gets a valid (refreshed if needed) access token, generates an SSO token,
   * and redirects to NexusChat.
   */
  const handleNavigateToNexus = async () => {
    console.log('[Nexus] handleNavigateToNexus called');
    console.log('[Nexus] isAuthenticated:', isAuthenticated);
    console.log('[Nexus] user:', user ? { id: user.id, email: user.email, hasToken: !!user.token, hasRefreshToken: !!user.refreshToken } : null);
    console.log('[Nexus] localStorage auth_token:', localStorage.getItem('auth_token') ? 'present (' + localStorage.getItem('auth_token').length + ' chars)' : 'MISSING');
    console.log('[Nexus] localStorage refresh_token:', localStorage.getItem('refresh_token') ? 'present' : 'MISSING');

    if (!isAuthenticated) {
      console.log('[Nexus] NOT authenticated — redirecting to login');
      sessionStorage.setItem('redirectAfterLogin', 'nexuschat');
      navigate('/login');
      return;
    }

    setIsNavigatingToNexus(true);

    try {
      // Get a valid access token (auto-refreshes if expired)
      console.log('[Nexus] Calling getValidToken()...');
      const token = await getValidToken();
      console.log('[Nexus] getValidToken returned:', token ? 'valid token (' + token.length + ' chars)' : 'NULL');

      if (!token) {
        // Refresh failed — user must re-login
        console.log('[Nexus] Token is null — redirecting to login');
        sessionStorage.setItem('redirectAfterLogin', 'nexuschat');
        navigate('/login');
        return;
      }

      // Call SSO generate endpoint (endpoint is at root, not under /api)
      const baseUrl = stripApiSuffix(API_URL);
      const ssoUrl = `${baseUrl}/auth/sso/generate`;
      console.log('[Nexus] Calling SSO generate at:', ssoUrl);

      const response = await fetch(ssoUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ target_app: 'nexuschat' })
      });

      console.log('[Nexus] SSO generate response status:', response.status);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error('[Nexus] SSO generate failed:', response.status, errorBody);
        throw new Error('Failed to generate SSO token');
      }

      const data = await response.json();
      console.log('[Nexus] SSO generate response:', { success: data.success, hasRedirectUrl: !!data.redirect_url });

      if (data.success && data.redirect_url) {
        console.log('[Nexus] Redirecting to:', data.redirect_url);
        window.location.href = data.redirect_url;
      } else {
        throw new Error(data.message || 'SSO token generation failed');
      }
    } catch (error) {
      console.error('[Nexus] SSO navigation error:', error);
      // Plain-link fallback to the Nexus login page (never localhost) so the
      // visitor can sign in manually instead of landing on a dead localhost URL.
      window.location.href = NEXUSCHAT_LOGIN_URL;
    } finally {
      setIsNavigatingToNexus(false);
    }
  };

  return (
    <header className="sticky top-0 z-sticky bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-sm">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo — shared image logo (LOGO RULE), never the prototype wordmark */}
          <ReskinFonts />
          <VidexaLogo to="/" />

          {/* Center Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 flex-1 justify-center mx-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
              end
            >
              Home
            </NavLink>

            <NavLink
              to="/tpas"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
            >
              TPAs
            </NavLink>

            <NavLink
              to="/brokers"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
            >
              Brokers
            </NavLink>

            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
            >
              Pricing
            </NavLink>

            <NavLink
              to="/security"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
            >
              Security
            </NavLink>

            <NavLink
              to="/demo"
              className={({ isActive }) =>
                isActive
                  ? "text-[#0E3056] dark:text-white font-medium whitespace-nowrap"
                  : "text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap"
              }
            >
              Demo
            </NavLink>

            {/* Nexus link - navigates to NexusChat with SSO */}
            <button
              onClick={handleNavigateToNexus}
              disabled={isNavigatingToNexus}
              className="text-[#0E3056]/70 dark:text-gray-300 hover:text-[#0E3056] dark:hover:text-white whitespace-nowrap font-medium transition-colors disabled:opacity-50"
            >
              {isNavigatingToNexus ? 'Loading...' : 'Nexus'}
            </button>
          </nav>

          {/* Right side group: Theme + Auth */}
          <div className="flex items-center space-x-4 flex-shrink-0">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? '🌞' : '🌙'}
            </button>

            {/* Authentication Section */}
            {isAuthenticated ? (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap">
                  <UserCircleIcon className="w-8 h-8 flex-shrink-0" />
                  <span className="text-sm font-medium hidden xl:inline">{user?.email}</span>
                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">Work</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{user?.email}</p>
                      <div className="flex items-center mt-2 text-xs text-green-600 dark:text-green-400">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Sync is on
                      </div>
                    </div>
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => {
                              logout();
                              navigate('/logout');
                            }}
                            className={`${
                              active ? 'bg-gray-100 dark:bg-gray-700' : ''
                            } block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300`}
                          >
                            Sign out
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              <Link
                to="/login"
                className="text-[#0E3056] dark:text-gray-300 hover:text-[#0E3056]/70 dark:hover:text-white font-medium transition-colors whitespace-nowrap"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 
